import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core';

import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { DtModelsOpenerService } from '@app/pages/digital-twin/services/dt-models-opener.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ApolloModule } from 'apollo-angular';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { LocationService, Location } from '../../../services/location.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { IDtBuildingModel } from '@app/pages/digital-twin/services/dt-buildings-data.service';

interface CmmsEntity {
	location?: Location;
	locations?: Location[];
	pk?: string;
	workOrderId?: string;
	incidentId?: string;
	coordinates?: {
		x?: number;
		y?: number;
	};
	entityType?: string;
	buildingId?: string;
	entityId?: string;
	hasCoordinates?: boolean;
}

type MinimalDtBuildingModel = Pick<
	IDtBuildingModel,
	'id' | 'isFavorite' | 'format' | 'modelId'
>;

@Component({
	selector: 'open-dt-from-workspace',
	imports: [ApolloModule, MatIconModule, MatButtonModule, NgxTolgeeModule],
	templateUrl: './open-dt-from-workspace.component.html',
	styleUrls: ['./open-dt-from-workspace.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenDtFromWorkspaceComponent {
	private readonly notify: IconSnackBarService = inject(IconSnackBarService);
	private readonly dtModelsService = inject(DtModelsOpenerService);
	private readonly translateService = inject(TranslateService);
	private readonly locationService = inject(LocationService);

	@Input() entityName: string;
	@Input() multipleEntities = false;

	@Input() set entity(entity: CmmsEntity) {
		if (entity) {
			this.handleSingleEntity(entity);
		} else {
			this.disabledToolTip.set('no_entities_available');
			this.isButtonEnabled.set(false);
		}
	}

	@Input() set entities(entities: CmmsEntity[]) {
		if (entities?.length) {
			this.handleMultipleEntities(entities);
		} else {
			this.disabledToolTip.set('no_entities_available');
			this.isButtonEnabled.set(false);
		}
	}

	isButtonEnabled = signal(false);
	disabledToolTip = signal('no_entities_available');

	private processedEntities: CmmsEntity[] = [];

	openInDt(event: Event) {
		event.stopPropagation();

		// Don't try to open if disabled
		if (!this.isButtonEnabled()) {
			return;
		}

		const validEntities = this.getValidEntities();

		if (!validEntities.length) {
			this.notify.error(this.translateService.instant('no_entities_available'));
			return;
		}

		// Select the first building ID to work with (per requirement)
		const primaryBuildingId = validEntities[0].buildingId;

		this.dtModelsService
			.getDigitalTwinModelsDetails(primaryBuildingId)
			.pipe(
				catchError(error => {
					this.handleModelError(error);
					return of<MinimalDtBuildingModel[]>([]);
				})
			)
			.subscribe(models => this.openEntitiesInModel(models, primaryBuildingId));
	}

	private openEntitiesInModel(models: MinimalDtBuildingModel[], buildingId: string) {
		if (models.length === 0) {
			this.notify.error(this.translateService.instant('no_models_found_for_building'));
			return;
		}

		const cmmsMarkers = this.createCmmsMarkers();

		this.dtModelsService.openModelsIn3dViewer(
			this.getFavoriteModelsIds(models),
			buildingId,
			{ preselectedCmmsMarkers: cmmsMarkers }
		);
	}

	private handleSingleEntity(entity: CmmsEntity): void {
		const processedEntity = this.enhanceEntityWithBuildingInfo(entity);
		this.processedEntities = processedEntity ? [processedEntity] : [];
		this.updateButtonState();
	}

	private handleMultipleEntities(entities: CmmsEntity[]): void {
		this.processedEntities = entities
			.map(entity => this.enhanceEntityWithBuildingInfo(entity))
			.filter(Boolean) as CmmsEntity[];

		this.updateButtonState();
	}

	private getValidEntities(): CmmsEntity[] {
		return this.processedEntities.filter(entity => entity.buildingId && entity.entityId);
	}

	private createCmmsMarkers(): Record<string, string> {
		const markers: Record<string, string> = {};
		const validEntities = this.getValidEntities();
		const entityGroups = this.groupEntitiesByType(validEntities);

		Object.entries(entityGroups).forEach(([type, entities]) => {
			const entityIds = entities.map(entity => entity.entityId);
			markers[type] = entityIds.join(',');
		});

		return markers;
	}

	private groupEntitiesByType(entities: CmmsEntity[]): Record<string, CmmsEntity[]> {
		const groups: Record<string, CmmsEntity[]> = {};

		entities.forEach(entity => {
			const type = entity.entityType || this.entityName;

			if (!groups[type]) {
				groups[type] = [];
			}
			groups[type].push(entity);
		});

		return groups;
	}

	private enhanceEntityWithBuildingInfo(entity: CmmsEntity): CmmsEntity | null {
		const location = entity?.location || entity?.locations?.[0];

		if (!location) {
			this.disabledToolTip.set('no_location_found');
			return null;
		}

		try {
			const buildingId = this.locationService.findBuildingIdFromLocation(location);
			const entityId = this.determineEntityId(entity);

			if (!buildingId || !entityId) {
				this.disabledToolTip.set('no_building_id_or_entity_id');
				return null;
			}

			const hasCoordinates = entity.coordinates?.x != null;
			const entityType = entity.entityType || this.entityName;

			return {
				...entity,
				buildingId,
				entityId,
				hasCoordinates,
				entityType,
			};
		} catch (error) {
			console.error('Error enhancing entity with building info:', error);
			this.disabledToolTip.set('failed_to_process_location');
			return null;
		}
	}

	private determineEntityId(entity: CmmsEntity): string {
		if (entity.entityType === 'asset' || this.entityName === 'asset') {
			return entity.pk;
		}

		if (entity.workOrderId) {
			return entity.workOrderId;
		}

		if (entity.incidentId) {
			return entity.incidentId;
		}

		return entity.pk;
	}

	private updateButtonState() {
		const validEntities = this.getValidEntities();

		if (!validEntities.length) {
			this.isButtonEnabled.set(false);
			return;
		}

		this.isButtonEnabled.set(true);
	}

	private handleModelError(error: unknown) {
		this.notify.error(this.translateService.instant('failed_to_load_models'));
		console.error(error);
	}

	private getFavoriteModelsIds(models: MinimalDtBuildingModel[]): number[] {
		const prioritisedModels: MinimalDtBuildingModel[] =
			this.dtModelsService.getPrioritisedModels(models);

		return prioritisedModels.map(model => model.id);
	}
}
